import { useState } from "react";
import axios from "axios";
import { IconButton } from "@/subframe/components/IconButton";
import { IconWithBackground } from "@/subframe/components/IconWithBackground";
import { Button } from "@/subframe/components/Button";
import { AppDispatch, RootState } from "../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import { removeLabel } from "../../redux/reducers/authSlice";

interface DeleteLabelPopupProps {
  labelId: number | undefined;
  open: boolean;
  setIsLabelDeleteOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

function DeleteLabelPopup({
  labelId,
  open,
  setIsLabelDeleteOpen,
}: DeleteLabelPopupProps) {
  
  const { token } = useSelector((state: RootState) => state.auth);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dispatch = useDispatch<AppDispatch>();

  const handleDeleteLabel = async () => {
    if (!labelId) return;
    setLoading(true);
    setError(null);

    try {
      await axios.delete(
        `https://api.insightssystem.com/api:uYdlVwac/web/label_management/${labelId}`,
        { headers: { 'Content-Type': 'application/json', ...(token && { Authorization: token }) } }
      );
      dispatch(removeLabel(labelId));
      setIsLabelDeleteOpen(false);
    } catch (err) {
      setError("Failed to delete label");
    } finally {
      setLoading(false);
    }
  };

  // Don't render if modal is not open
  if (!open) return null;

  return (
    <>
      {/* Modal Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-[70] transition-opacity duration-300"
        onClick={() => setIsLabelDeleteOpen(false)}
      />
      
      {/* Modal Content */}
      <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
          {/* Modal Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center gap-3">
              <IconWithBackground
                variant="error"
                size="large"
                icon="FeatherTrash2"
              />
              <h2 className="text-xl font-bold text-gray-900">Delete Label?</h2>
            </div>
            <IconButton
              variant="brand-tertiary"
              size="large"
              icon="FeatherX"
              onClick={() => setIsLabelDeleteOpen(false)}
              className="text-gray-500 hover:text-gray-700"
            />
          </div>

          {/* Modal Body */}
          <div className="flex-1 p-6">
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-sm text-gray-600">
                  This label will be permanently deleted for all users across all
                  locations.
                </p>
              </div>

              {error && (
                <div className="text-center">
                  <span className="text-red-500 text-sm">{error}</span>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  variant="brand-secondary"
                  onClick={() => setIsLabelDeleteOpen(false)}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive-primary"
                  onClick={handleDeleteLabel}
                  disabled={loading}
                >
                  {loading ? "Deleting..." : "Delete"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default DeleteLabelPopup;
